/**
 * The Guide — SSE stream consumer.
 *
 * The transport is the platform's one `streamSSE` (`@services/sse`); what
 * belongs to the Guide is the event union below. It streams through the same
 * server helper as the Concierge and the meta-agent, so its events are the
 * same shapes — plus one of its own, `tutorial`, which the Learn page turns
 * into "select this tutorial and play it".
 */

import { streamSSE, type SseOptions } from '@services/sse';

export interface GuideMessage {
  message_id: string;
  sender_type: 'user' | 'assistant' | 'system';
  message_content: string;
  created_at: string;
}

/** The tutorial the Guide opened, as emitted by its `show_tutorial` tool. */
export interface GuideTutorialEvent {
  type: 'tutorial';
  id: string;
  title?: { en: string; he: string } | string | null;
  section?: string | null;
  film?: boolean;
  why?: string | null;
}

export type GuideStreamEvent =
  | { type: 'user_message'; message: GuideMessage; conversation_title?: string }
  | { type: 'thinking'; status?: string }
  | { type: 'ai_chunk'; content: string }
  | GuideTutorialEvent
  | { type: 'ai_complete'; message: GuideMessage; title_update?: string }
  | { type: 'error'; error?: string }
  | { type: 'done'; aborted?: boolean };

export function streamGuideMessage(
  conversationId: string,
  payload: { message: string; model?: string },
  options?: SseOptions,
): AsyncGenerator<GuideStreamEvent, void, undefined> {
  return streamSSE<GuideStreamEvent>(
    `/guide/conversations/${conversationId}/stream`,
    payload,
    options,
  );
}
